/**
 * Analytics · Anomaly Detection
 * Demo content - see src/demo/analytics.ts.
 */

import React from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, Kpi, PageHead, Table, Tag, Note, DemoBadge } from '../../components/ui';
import { ChartFrame, Trend } from '../../components/charts';
import { ANOMALIES } from '../../demo/analytics';

export const AnomalyDetectionPage: React.FC = () => (
  <AppShell
    title="Anomaly Detection"
    sub="Analytics"
    actions={
      <>
        <DemoBadge />
        <button className="btn btn-g">Detection settings</button>
      </>
    }
  >
    <PageHead
      eyebrow="Analytics"
      title="What broke its own pattern"
      lede="Each metric is modelled against its own recent history. A figure is flagged when it leaves the expected range by enough to matter — with the expected band shown, so you can judge the call yourself."
    />

    <div className="g g4" style={{ marginBottom: 12 }}>
      {ANOMALIES.kpis.map((k) => (
        <Kpi
          key={k.label}
          label={k.label}
          value={k.value}
          unit={'unit' in k ? (k as any).unit : undefined}
          color={k.color}
          icon={k.icon as any}
          sub={'sub' in k ? (k as any).sub : undefined}
        />
      ))}
    </div>

    <div className="g g3" style={{ marginBottom: 12 }}>
      <div className="s2">
        <ChartFrame title="Observed against expected" sub="query volume, last 7 days — the gap on Thu/Fri is the flag">
          <Trend
            data={ANOMALIES.detection}
            x="day"
            series={[
              { key: 'expected', label: 'Expected' },
              { key: 'observed', label: 'Observed' },
            ]}
          />
        </ChartFrame>
      </div>

      <Card title="How a flag is raised">
        <div className="dreq">
          <span className="st2" style={{ background: 'var(--blue-s)', color: 'var(--blue-d)' }}>
            1
          </span>
          <div>
            <div style={{ fontWeight: 700 }}>Model the metric's own range</div>
            <div className="t2">Seasonality and weekday effects are fitted per metric.</div>
          </div>
        </div>
        <div className="dreq">
          <span className="st2" style={{ background: 'var(--amber-s)', color: '#8A5C05' }}>
            2
          </span>
          <div>
            <div style={{ fontWeight: 700 }}>Compare the observation</div>
            <div className="t2">Deviation is reported in standard deviations, not percentages.</div>
          </div>
        </div>
        <div className="dreq">
          <span className="st2" style={{ background: 'var(--green-s)', color: '#0A7A55' }}>
            3
          </span>
          <div>
            <div style={{ fontWeight: 700 }}>Check it against the source</div>
            <div className="t2">A flag names the system and record that produced it.</div>
          </div>
        </div>
        <Note style={{ marginTop: 10 }}>
          Anomalies are surfaced, never auto-corrected. Nothing is written back to a source system.
        </Note>
      </Card>
    </div>

    <Card title="Detected anomalies" sub="most severe first" flush>
      <Table
        cols={[
          { t: 'Severity' },
          { t: 'Metric' },
          { t: 'Observed', num: true },
          { t: 'Expected', num: true },
          { t: 'Deviation', num: true },
          { t: 'Source' },
          { t: 'Detected' },
          { t: 'Status' },
        ]}
        rows={ANOMALIES.rows.map((a) => [
          <Tag tone={a.tone as any} dot>
            {a.severity}
          </Tag>,
          <b>{a.metric}</b>,
          a.observed,
          <span className="dim">{a.expected}</span>,
          <span className="mono" style={{ fontWeight: 700 }}>
            {a.deviation}
          </span>,
          a.source,
          <span className="dim">{a.when}</span>,
          a.status,
        ])}
      />
    </Card>
  </AppShell>
);
